'use client';

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import type {
  Session,
  AccountSwitcherConfig,
  AccountSwitcherContextValue,
  ConfirmPayload,
  PromptPayload,
  AlertPayload,
  UrlRedirectPayload,
} from '../lib/types';
import { PostMessageClient } from '../lib/client';
import { MESSAGE_TYPES } from '../lib/message-protocol';
import { DialogManager } from '../lib/dialogs';

// Cross-bundle singleton context.
//
// tsup builds this package as multiple entry points (index, components, hooks,
// sharing) with `splitting: false`, which DUPLICATES this module — and its
// `createContext()` call — into every entry bundle. That yields distinct
// Context objects, so a Provider imported from one entry (e.g. the root) and a
// consumer imported from another (e.g. `/sharing`'s HandoffLanding) never
// match, and the consumer reads `null`. Anchoring the Context on `globalThis`
// behind a stable `Symbol.for()` key guarantees exactly ONE shared Context
// object across all bundles at runtime. See tsup.config.ts.
const CONTEXT_KEY = Symbol.for('@benbraide/auth-service-nextjs/AccountSwitcherContext');
type AccountSwitcherReactContext = React.Context<AccountSwitcherContextValue | null>;
const contextRegistry = globalThis as unknown as Record<symbol, AccountSwitcherReactContext | undefined>;
const AccountSwitcherContext: AccountSwitcherReactContext =
  contextRegistry[CONTEXT_KEY] ??
  (contextRegistry[CONTEXT_KEY] = createContext<AccountSwitcherContextValue | null>(null));

export interface AccountSwitcherProviderProps extends AccountSwitcherConfig {
  children: React.ReactNode;
}

/**
 * Provider component for account switcher functionality
 * Wraps your app to provide session state and switcher methods
 */
export function AccountSwitcherProvider({
  children,
  backendUrl,
  authServiceUrl,
  serviceApiKey,
  onSessionChange,
  onAccountChange,
  onError,
  debug = false,
  dialogs,
  onConfirmRequest,
  onPromptRequest,
  onAlertRequest,
  autoResize = true,
  minHeight = 200,
  maxHeight = null,
  onResize,
  onNavigate,
  onNavigateToAddAccount,
  onNavigateToManageAccount,
  onNavigateToAvatarUpdate,
}: AccountSwitcherProviderProps) {
  const [session, setSession] = useState<Session>({
    isAuthenticated: false,
    currentUser: null,
    accounts: [],
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  // State mirror of clientRef so the context value updates (and consumers such
  // as HandoffLanding re-render) once the PostMessage client is initialized in
  // the mount effect below.
  const [activeClient, setActiveClient] = useState<PostMessageClient | null>(null);
  const clientRef = useRef<PostMessageClient | null>(null);
  const dialogManagerRef = useRef<DialogManager | null>(null);

  // Use refs for callbacks to avoid recreating PostMessageClient on every prop change
  const debugRef = useRef(debug);
  const onSessionChangeRef = useRef(onSessionChange);
  const onAccountChangeRef = useRef(onAccountChange);
  const onErrorRef = useRef(onError);
  const onConfirmRequestRef = useRef(onConfirmRequest);
  const onPromptRequestRef = useRef(onPromptRequest);
  const onAlertRequestRef = useRef(onAlertRequest);
  const onNavigateRef = useRef(onNavigate);
  const onNavigateToAddAccountRef = useRef(onNavigateToAddAccount);
  const onNavigateToManageAccountRef = useRef(onNavigateToManageAccount);
  const onNavigateToAvatarUpdateRef = useRef(onNavigateToAvatarUpdate);

  // Update refs whenever callbacks change
  useEffect(() => {
    debugRef.current = debug;
    onSessionChangeRef.current = onSessionChange;
    onAccountChangeRef.current = onAccountChange;
    onErrorRef.current = onError;
    onConfirmRequestRef.current = onConfirmRequest;
    onPromptRequestRef.current = onPromptRequest;
    onAlertRequestRef.current = onAlertRequest;
    onNavigateRef.current = onNavigate;
    onNavigateToAddAccountRef.current = onNavigateToAddAccount;
    onNavigateToManageAccountRef.current = onNavigateToManageAccount;
    onNavigateToAvatarUpdateRef.current = onNavigateToAvatarUpdate;
  });

  // Initialize PostMessage client
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Initialize dialog manager if dialogs are enabled
    if (dialogs?.enabled !== false) {
      const dialogManager = new DialogManager(dialogs || {});
      dialogManager.injectDialogStyles();
      dialogManagerRef.current = dialogManager;
    }

    const config: AccountSwitcherConfig = {
      backendUrl,
      authServiceUrl,
      serviceApiKey,
      onSessionChange,
      onAccountChange,
      onError,
      debug,
      dialogs,
      onConfirmRequest,
      onPromptRequest,
      onAlertRequest,
      autoResize,
      minHeight,
      maxHeight,
      onResize,
    };

    const client = new PostMessageClient(config);
    clientRef.current = client;
    // Publish into state so the context value exposes a live `client` to
    // consumers (e.g. the cross-product handoff) once it's ready.
    setActiveClient(client);

    // Set up session change listener
    client.on(MESSAGE_TYPES.SESSION_CHANGED, (data: any) => {
      const sessionData = data.payload || data;
      const newSession: Session = {
        isAuthenticated: sessionData.isAuthenticated || false,
        currentUser: sessionData.currentUser || null,
        accounts: sessionData.accounts || [],
      };

      setSession(newSession);
      onSessionChangeRef.current?.(newSession);
    });

    // Set up account change listener
    client.on(MESSAGE_TYPES.ACCOUNT_SWITCHED, (data: any) => {
      const payload = data.payload || data;

      // ACCOUNT_SWITCHED only sends { user_uuid }, not the full user object
      // Find the full user object from the accounts array
      setSession((prevSession) => {
        const switchedUser = prevSession.accounts.find(
          (account) => account.uuid === payload.user_uuid
        );

        if (switchedUser) {
          // Update current user with the full user object from accounts
          const newSession = {
            ...prevSession,
            currentUser: switchedUser,
          };

          // Call the callback with the full user object
          onAccountChangeRef.current?.(switchedUser);

          return newSession;
        }

        // If user not found in accounts, keep previous session
        if (debug) {
          console.warn('[AccountSwitcher] Switched user not found in accounts:', payload.user_uuid);
        }
        return prevSession;
      });
    });

    // Set up error listener
    client.on(MESSAGE_TYPES.ERROR, (data: any) => {
      const err = data.payload || data;
      const error = new Error(err.message || 'Unknown error');
      setError(error);
      onErrorRef.current?.(error);
    });

    // NOTE: SIZE_CHANGED is now handled directly in AccountSwitcher component
    // using direct DOM manipulation to avoid re-renders. If you need resize notifications,
    // use the onHeightChange prop on the AccountSwitcher component instead.

    // Set up navigation handlers
    client.on(MESSAGE_TYPES.ADD_ACCOUNT, (data: any) => {
      const payload = data.payload || data;
      if (payload.landing_url) {
        if (onNavigateToAddAccountRef.current) {
          onNavigateToAddAccountRef.current(payload.landing_url);
        } else {
          window.location.href = payload.landing_url;
        }
      }
    });

    client.on(MESSAGE_TYPES.MANAGE_ACCOUNT, (data: any) => {
      const payload = data.payload || data;
      if (payload.management_url) {
        if (onNavigateToManageAccountRef.current) {
          onNavigateToManageAccountRef.current(payload.management_url);
        } else {
          window.location.href = payload.management_url;
        }
      }
    });

    client.on(MESSAGE_TYPES.UPDATE_AVATAR, (data: any) => {
      const payload = data.payload || data;
      if (payload.avatar_url) {
        if (onNavigateToAvatarUpdateRef.current) {
          onNavigateToAvatarUpdateRef.current(payload.avatar_url);
        } else {
          window.location.href = payload.avatar_url;
        }
      }
    });

    // Set up unified URL redirect handler (Unified Action Protocol)
    client.on(MESSAGE_TYPES.URL_REDIRECT, (data: any) => {
      const payload = data.payload || data;
      const redirectPayload: UrlRedirectPayload = {
        url: payload.url,
        reason: payload.reason,
        openInNewTab: payload.openInNewTab || false,
      };

      if (onNavigateRef.current) {
        // Use new unified navigation handler
        onNavigateRef.current(redirectPayload);
      } else {
        // Fallback to legacy handlers or default behavior
        const { url, reason, openInNewTab } = redirectPayload;

        // Try legacy handlers first based on reason
        if (reason === 'add-account' && onNavigateToAddAccountRef.current) {
          onNavigateToAddAccountRef.current(url);
        } else if (reason === 'manage-account' && onNavigateToManageAccountRef.current) {
          onNavigateToManageAccountRef.current(url);
        } else if (reason === 'update-avatar' && onNavigateToAvatarUpdateRef.current) {
          onNavigateToAvatarUpdateRef.current(url);
        } else {
          // Default navigation behavior
          if (openInNewTab) {
            window.open(url, '_blank');
          } else {
            window.location.href = url;
          }
        }
      }
    });

    // Set up dialog handlers
    if (dialogs?.enabled !== false) {
      // Confirm dialog
      client.on(MESSAGE_TYPES.CONFIRM_REQUEST, async (data: any) => {
        if (debug) {
          console.log('[AccountSwitcher] CONFIRM_REQUEST received:', data);
        }
        const { requestId, payload } = data;

        try {
          let result: boolean;

          if (onConfirmRequestRef.current) {
            // Use custom handler if provided
            result = await onConfirmRequestRef.current(payload as ConfirmPayload);
          } else if (dialogManagerRef.current) {
            // Use custom dialog manager
            result = await dialogManagerRef.current.showConfirm(payload as ConfirmPayload);
          } else {
            // Fallback to browser confirm
            result = window.confirm(payload.message);
          }

          client.sendDialogResponse(MESSAGE_TYPES.REQUEST_RESPONSE, requestId, {
            confirmed: result,
          });
        } catch (err) {
          client.sendDialogResponse(MESSAGE_TYPES.REQUEST_RESPONSE, requestId, {
            confirmed: false,
          });
        }
      });

      // Prompt dialog
      client.on(MESSAGE_TYPES.PROMPT_REQUEST, async (data: any) => {
        const { requestId, payload } = data;

        try {
          let value: string | null = null;
          let cancelled = true;

          if (onPromptRequestRef.current) {
            // Use custom handler if provided
            const customResult = await onPromptRequestRef.current(payload as PromptPayload);
            value = customResult;
            cancelled = customResult === null;
          } else if (dialogManagerRef.current) {
            // Use custom dialog manager
            const result = await dialogManagerRef.current.showPrompt(payload as PromptPayload);
            value = result.value;
            cancelled = result.cancelled;
          } else {
            // Fallback to browser prompt
            const browserResult = window.prompt(payload.message, payload.defaultValue || '');
            value = browserResult;
            cancelled = browserResult === null;
          }

          client.sendDialogResponse(MESSAGE_TYPES.REQUEST_RESPONSE, requestId, {
            value,
            cancelled,
          });
        } catch (err) {
          client.sendDialogResponse(MESSAGE_TYPES.REQUEST_RESPONSE, requestId, {
            value: null,
            cancelled: true,
          });
        }
      });

      // Alert dialog
      client.on(MESSAGE_TYPES.ALERT_REQUEST, async (data: any) => {
        const { requestId, payload } = data;

        try {
          if (onAlertRequestRef.current) {
            // Use custom handler if provided
            await onAlertRequestRef.current(payload as AlertPayload);
          } else if (dialogManagerRef.current) {
            // Use custom dialog manager
            await dialogManagerRef.current.showAlert(payload as AlertPayload);
          } else {
            // Fallback to browser alert
            window.alert(payload.message);
          }

          client.sendDialogResponse(MESSAGE_TYPES.REQUEST_RESPONSE, requestId, {
            acknowledged: true,
          });
        } catch (err) {
          client.sendDialogResponse(MESSAGE_TYPES.REQUEST_RESPONSE, requestId, {
            acknowledged: false,
          });
        }
      });
    }

    return () => {
      client.destroy();
      clientRef.current = null;
      setActiveClient(null);
    };
  }, []);

  const switchAccount = useCallback(async (userUuid: string) => {
    if (!clientRef.current) throw new Error('Client not initialized');

    setIsLoading(true);
    setError(null);

    try {
      await clientRef.current.switchAccount(userUuid);
    } catch (err) {
      const error = err as Error;
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const addAccount = useCallback(async () => {
    if (!clientRef.current) throw new Error('Client not initialized');

    try {
      await clientRef.current.addAccount();
    } catch (err) {
      const error = err as Error;
      setError(error);
      throw error;
    }
  }, []);

  const manageAccount = useCallback(async () => {
    if (!clientRef.current) throw new Error('Client not initialized');

    try {
      await clientRef.current.manageAccount();
    } catch (err) {
      const error = err as Error;
      setError(error);
      throw error;
    }
  }, []);

  const updateAvatar = useCallback(async () => {
    if (!clientRef.current) throw new Error('Client not initialized');

    try {
      await clientRef.current.updateAvatar();
    } catch (err) {
      const error = err as Error;
      setError(error);
      throw error;
    }
  }, []);

  const logoutAccount = useCallback(async (userUuid: string) => {
    if (!clientRef.current) throw new Error('Client not initialized');

    setIsLoading(true);
    setError(null);

    try {
      await clientRef.current.logoutAccount(userUuid);
    } catch (err) {
      const error = err as Error;
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logoutAll = useCallback(async () => {
    if (!clientRef.current) throw new Error('Client not initialized');

    setIsLoading(true);
    setError(null);

    try {
      await clientRef.current.logoutAll();
    } catch (err) {
      const error = err as Error;
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refresh = useCallback(async () => {
    if (!clientRef.current) throw new Error('Client not initialized');

    setIsLoading(true);
    setError(null);

    try {
      const sessionData = await clientRef.current.getSession();
      setSession(sessionData);
    } catch (err) {
      const error = err as Error;
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const registerIframe = useCallback((iframe: HTMLIFrameElement, origin: string) => {
    if (!clientRef.current) {
      console.warn('[AccountSwitcherProvider] Client not initialized, cannot register iframe');
      return;
    }

    clientRef.current.setIframe(iframe, origin);

    // Fetch initial session after iframe is registered
    // Wait a bit for the iframe to be ready and handshake to complete
    setTimeout(async () => {
      try {
        setIsLoading(true);
        const sessionData = await clientRef.current!.getSession();
        setSession(sessionData);
      } catch (err) {
        if (debugRef.current) {
          console.error('[AccountSwitcherProvider] Failed to fetch initial session:', err);
        }
      } finally {
        setIsLoading(false);
      }
    }, 1000); // Give the iframe time to complete handshake
  }, []); // No dependencies - uses only refs and setState

  const sendMessage = useCallback((type: string, payload?: unknown) => {
    if (!clientRef.current) {
      console.warn('[AccountSwitcherProvider] Client not initialized, cannot send message:', type);
      return;
    }
    clientRef.current.send(type, payload);
  }, []);

  const value: AccountSwitcherContextValue = {
    session,
    isLoading,
    error,
    switchAccount,
    addAccount,
    manageAccount,
    updateAvatar,
    logoutAccount,
    logoutAll,
    refresh,
    registerIframe,
    client: activeClient,
    sendMessage,
  };

  return (
    <AccountSwitcherContext.Provider value={value}>
      {children}
    </AccountSwitcherContext.Provider>
  );
}

/**
 * Hook to access the account switcher context
 * @throws Error if used outside of AccountSwitcherProvider
 */
export function useAccountSwitcherContext() {
  const context = useContext(AccountSwitcherContext);

  if (!context) {
    throw new Error(
      'useAccountSwitcherContext must be used within AccountSwitcherProvider'
    );
  }

  return context;
}
